import { useMemo, useEffect } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import useMerchandiseList from 'hooks/Merchandise/useMerchandiseList'
import { getMerchandiseFiltersFromQuery } from './merchandiseSearchPageUtils'
import useMerchandiseCategory from 'hooks/Merchandise/useMerchandiseCategory'
import useMerchandiseBrand from 'hooks/Merchandise/useMerchandiseBrand'
import MerchandiseSearchPageNoResults from './MerchandiseSearchPageNoResults'
import MerchandiseSearchPageResults from './MerchandiseSearchPageResults'
import { useAppDispatch } from 'hooks/reduxHooks'
import { replaceWithRegion } from 'actions/NavigationActions'
import { getMerchandiseBrandSearchUrl } from '../merchandiseUtils'

function MerchandiseSearchPage() {
  const location = useLocation()
  const dispatch = useAppDispatch()
  const { categorySlug, brandId, brandSlug } = useParams<{ parentCategorySlug?: string, categorySlug?: string, brandSlug?: string, brandId?: string }>()
  const urlFilters = useMemo(() => getMerchandiseFiltersFromQuery(location.search), [location.search])

  // Brand can come from URL path (brandId param) or query string (urlFilters.brands)
  const brandIdToFetch = brandId || urlFilters.brands?.[0]
  const [brand, fetchingBrand] = useMerchandiseBrand(brandIdToFetch)
  const [category, fetchingCategory] = useMerchandiseCategory(categorySlug ? { slug: categorySlug } : { id: urlFilters.categories?.[0] })

  // Validate brand slug and redirect if it doesn't match
  useEffect(() => {
    if (brandSlug && brandId && brand && !fetchingBrand) {
      // Check if the slug in the URL matches the actual brand slug
      if (brand.slug !== brandSlug) {
        // Redirect to the correct URL with the proper slug
        const correctUrl = getMerchandiseBrandSearchUrl(brand, location.search.substring(1))
        dispatch(replaceWithRegion(correctUrl))
      }
    }
  }, [brandSlug, brandId, brand, fetchingBrand, location.search, dispatch])

  const topRootCategory = useMemo(
    () => category?.ancestors?.[category?.ancestors?.length - 1]?.id,
    [category],
  )
  const [parentCategory] = useMerchandiseCategory({ id: topRootCategory }, { disabled: !topRootCategory })

  const urlPathSlug = brandSlug ?? categorySlug

  const filters = useMemo((): App.MerchandiseFilters => {
    // If user selected categories via query params, use those; otherwise use category from URL slug
    const categories = urlFilters.categories?.length
      ? urlFilters.categories
      : category
        ? [category.id]
        : []

    // If brand comes from URL path, use it; otherwise use brands from query params
    const brands = brandId
      ? [brandId]
      : urlFilters.brands || []

    return {
      ...urlFilters,
      categories,
      brands,
    }
  }, [urlFilters, category, brandId])

  const [list, fetching, error] = useMerchandiseList(filters)

  const isInvalidFilters = (!!categorySlug && !category && !fetchingCategory) || (!!brandIdToFetch && !brand && !fetchingBrand)
  const pageFetching = (fetching || fetchingCategory || fetchingBrand) && !error

  // Show NoResults page only for base searches with no results, not filter-induced empty states
  const showNoResultsPage = useMemo(() => {
    const hasUserAppliedFilters = !!(
      filters.price ||
      filters.points ||
      filters.campaigns?.length ||
      filters.options?.length ||
      urlFilters.categories?.length
    )

    return (
      !pageFetching &&
      !list.length &&
      (
        !hasUserAppliedFilters ||
        isInvalidFilters ||
        !!error
      )
    )
  }, [pageFetching, list.length, error, isInvalidFilters, filters.price, filters.points, filters.campaigns, filters.options, urlFilters.categories])

  return <>
    {showNoResultsPage && <MerchandiseSearchPageNoResults
      category={category}
      brand={brand}
      search={urlFilters.name}
      showBreadcrumbs
    />}
    {!showNoResultsPage && !isInvalidFilters && <MerchandiseSearchPageResults
      filters={filters}
      searchResults={list}
      fetching={pageFetching}
      category={category}
      parentCategory={parentCategory || category}
      brand={brand}
      urlPathSlug={urlPathSlug}
    />}
  </>
}

export default MerchandiseSearchPage
